import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, IconButton, Chip, useMediaQuery } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material/styles";
import StatCard from "../StatCard";
import { useDraftData } from "../../context/DraftDataContext";
import { useAdp } from "../../context/AdpContext";
import { useMarketAdp } from "../../helpers/useMarketAdp";
import { groupDataByPlayer } from "../../helpers/playerHelpers";
import { computePlayerCLVMulti, getTeamCLV } from "../../helpers/playerHelpers";
import { getTeamsTableFromCsv } from "../../helpers/chartHelpers";
import { shadows as ds, typeVariant } from "../../config/theme";
import { useStatusColors } from "../../helpers/statusColors";
import { ADP_FROZEN_AT } from "../../config/season";

const PAGE_LABELS: Record<string, string> = {
  "/players": "My Players",
  "/teams": "My Teams",
  "/adp-trends": "ADP Trends",
  "/dashboard": "Dashboard",
  "/insights": "Insights",
};

const PAGE_SUBTITLES: Record<string, string> = {
  "/dashboard": "Your portfolio at a glance",
  "/players": "Exposure, ADP, and combos across every draft",
  "/teams": "Every entry you built",
  "/adp-trends": "Your picks vs the market",
  "/schedule": "Bye and playoff concentration",
  "/insights": "Stacks, archetypes, and leverage",
};

// Pages that render their own header/KPIs in-page (no PageHeader KPI strip).
const NO_KPI_PATHS = new Set(["/dashboard", "/schedule", "/insights"]);

const KPI_STRIP_SX = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, minmax(0, 1fr))",
    sm: "repeat(3, minmax(0, 1fr))",
    md: "repeat(auto-fit, minmax(140px, 1fr))",
  },
  gap: { xs: 1, md: 1.5 },
  alignItems: "stretch",
} as const;

function formatDate(): string {
  return new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

// ── Per-page KPI strips ───────────────────────────────────────────

function PlayersKPIs() {
  const { filteredRawData, rawData } = useDraftData();
  const { lookupForProvider } = useMarketAdp();

  const { playerCount, totalDrafts, avgClv, topPlayer } = useMemo(() => {
    if (!rawData.length) return { playerCount: 0, totalDrafts: 0, avgClv: null, topPlayer: null };

    const playersData = groupDataByPlayer(filteredRawData);
    const drafts = new Set(filteredRawData.map(r => r["Draft"])).size;

    let clvWeightedSum = 0;
    let clvWeightTotal = 0;
    let topName = "";
    let topExposure = 0;

    for (const p of playersData) {
      // Each pick scored against its own platform's market, then draft-weighted.
      const { avgClv: playerClv } = computePlayerCLVMulti(p.name, p.rows, lookupForProvider);
      const playerDrafts = new Set(p.rows.map(r => r["Draft"])).size;
      if (playerClv !== null && playerDrafts > 0) {
        clvWeightedSum += playerClv * playerDrafts;
        clvWeightTotal += playerDrafts;
      }

      const exposure = drafts > 0 ? (playerDrafts / drafts) * 100 : 0;
      if (exposure > topExposure) { topExposure = exposure; topName = p.name; }
    }

    return {
      playerCount: playersData.length,
      totalDrafts: drafts,
      avgClv: clvWeightTotal > 0 ? clvWeightedSum / clvWeightTotal : null,
      topPlayer: topName ? { name: topName, exposure: topExposure } : null,
    };
  }, [filteredRawData, rawData, lookupForProvider]);

  const sc = useStatusColors();

  if (!rawData.length) return null;

  return (
    <Box sx={KPI_STRIP_SX}>
      <StatCard metric={playerCount} label="players" />
      <StatCard metric={totalDrafts} label="drafts" />
      <StatCard
        metric={avgClv !== null ? (avgClv > 0 ? "+" : "") + avgClv.toFixed(1) : "—"}
        label="weighted avg CLV"
        accentColor={avgClv !== null ? (avgClv >= 0 ? sc.good : sc.bad) : undefined}
      />
      {topPlayer && (
        <StatCard
          metric={topPlayer.name}
          label={`top exposure · ${topPlayer.exposure.toFixed(1)}%`}
        />
      )}
    </Box>
  );
}

function TeamsKPIs() {
  const { filteredRawData, rawData } = useDraftData();
  const { lookupForProvider } = useMarketAdp();

  const { teamCount, avgClv, bestClv, worstClv } = useMemo(() => {
    if (!rawData.length) return { teamCount: 0, avgClv: null, bestClv: null, worstClv: null };

    const teams = getTeamsTableFromCsv(filteredRawData);
    if (!teams.length) return { teamCount: 0, avgClv: null, bestClv: null, worstClv: null };

    // Each draft is single-platform → score it on that platform's market.
    const clvValues = teams.map(t => getTeamCLV(t, lookupForProvider(t.provider)).totalCLV);
    const avg = clvValues.reduce((a, b) => a + b, 0) / clvValues.length;
    const best = Math.max(...clvValues);
    const worst = Math.min(...clvValues);

    return { teamCount: teams.length, avgClv: avg, bestClv: best, worstClv: worst };
  }, [filteredRawData, rawData, lookupForProvider]);

  const sc = useStatusColors();

  if (!rawData.length) return null;

  return (
    <Box sx={KPI_STRIP_SX}>
      <StatCard metric={teamCount} label="drafts" />
      <StatCard
        metric={avgClv !== null ? (avgClv > 0 ? "+" : "") + avgClv.toFixed(1) : "—"}
        label="avg CLV / draft"
        accentColor={avgClv !== null ? (avgClv >= 0 ? sc.good : sc.bad) : undefined}
      />
      <StatCard
        metric={bestClv !== null ? (bestClv > 0 ? "+" : "") + bestClv.toFixed(1) : "—"}
        label="best draft CLV"
        accentColor={sc.good}
      />
      <StatCard
        metric={worstClv !== null ? (worstClv > 0 ? "+" : "") + worstClv.toFixed(1) : "—"}
        label="worst draft CLV"
        accentColor={sc.bad}
      />
    </Box>
  );
}

function AdpTrendsKPIs() {
  const { adpHistory, adpHistoryLoading, syncedAt } = useAdp();

  const { biggestRiser, biggestFaller, trackedCount, lastUpdated } = useMemo(() => {
    if (!adpHistory.length) return { biggestRiser: null, biggestFaller: null, trackedCount: 0, lastUpdated: null };

    // Anchor the 7-day window to the newest snapshot in the data rather than to
    // wall-clock now. While the scrape is live those are the same day, so this
    // changes nothing; once it is frozen (ADP_FROZEN_AT) a wall-clock window
    // walks off the end of the history, both ends resolve to the final snapshot
    // and every delta collapses to zero — which renders as "biggest riser · +0.0"
    // on some arbitrary player.
    let latestDate: string | null = null;
    for (const p of adpHistory) {
      const latest = p.history[p.history.length - 1];
      if (latest && (!latestDate || latest.date > latestDate)) latestDate = latest.date;
    }
    const anchorDate = latestDate ?? new Date().toISOString().slice(0, 10);
    const ago7 = new Date(new Date(anchorDate + "T12:00:00Z").getTime() - 7 * 86400000)
      .toISOString()
      .slice(0, 10);

    let bestRiser: { name: string; delta: number } | null = null;
    let bestFaller: { name: string; delta: number } | null = null;

    for (const p of adpHistory) {
      if (!p.history.length) continue;

      const curr = p.history.filter(h => h.date <= anchorDate).slice(-1)[0]?.adp ?? null;
      const prev = p.history.filter(h => h.date <= ago7).slice(-1)[0]?.adp ?? null;
      if (curr === null || prev === null) continue;

      const delta = prev - curr; // positive = riser
      if (!bestRiser || delta > bestRiser.delta) bestRiser = { name: p.name, delta };
      if (!bestFaller || delta < bestFaller.delta) bestFaller = { name: p.name, delta };
    }

    return {
      biggestRiser: bestRiser,
      biggestFaller: bestFaller,
      trackedCount: adpHistory.length,
      lastUpdated: syncedAt
        ? new Date(syncedAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/New_York", timeZoneName: "short" })
        : latestDate
          ? new Date(latestDate + "T12:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric" })
          : null,
    };
  }, [adpHistory, syncedAt]);

  const sc = useStatusColors();

  if (adpHistoryLoading) return null;

  return (
    <Box sx={KPI_STRIP_SX}>
      {biggestRiser && (
        <StatCard
          metric={biggestRiser.name}
          label={`biggest riser · +${biggestRiser.delta.toFixed(1)} Δ7d`}
          accentColor={sc.good}
        />
      )}
      {biggestFaller && (
        <StatCard
          metric={biggestFaller.name}
          label={`biggest faller · ${biggestFaller.delta.toFixed(1)} Δ7d`}
          accentColor={sc.bad}
        />
      )}
      <StatCard metric={trackedCount} label="players tracked" />
      {lastUpdated && (
        <StatCard
          metric={lastUpdated}
          // A frozen feed is not a stalled one. Say so, or a date that never
          // moves again reads as a broken pipeline. "drafts closed" rather than
          // a week-1 date: this is the day Underdog stopped taking drafts,
          // which is what the board actually records.
          label={ADP_FROZEN_AT ? "final · drafts closed" : "last updated"}
        />
      )}
    </Box>
  );
}

// ── Main PageHeader ───────────────────────────────────────────────

interface PageHeaderProps {
  onOpenMobileMenu?: () => void;
}

export default function PageHeader({ onOpenMobileMenu }: PageHeaderProps) {
  const location = useLocation();
  const theme = useTheme();
  const {
    teamFilter, setTeamFilter, providersPresent, rawData,
    providerFilter, setProviderFilter, hasMultipleProviders,
  } = useDraftData();
  const hasData = rawData.length > 0;
  const knownProviders = providersPresent.filter(p => p !== "Unknown");
  const providerLabel =
    knownProviders.length === 0 ? "Uploaded CSV" :
    knownProviders.join(" + ");
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const path = location.pathname;
  const title = PAGE_LABELS[path] ?? "rosterOS";
  const subtitle = PAGE_SUBTITLES[path];
  const showMobileMenuButton = isMobile && typeof onOpenMobileMenu === "function";

  // Some pages render their own header/KPIs in-page
  const showKpis = !NO_KPI_PATHS.has(path);

  let kpis: React.ReactNode = null;
  if (path === "/players") kpis = <PlayersKPIs />;
  else if (path === "/teams") kpis = <TeamsKPIs />;
  else if (path === "/adp-trends") kpis = <AdpTrendsKPIs />;

  return (
    <Box
      sx={{
        px: { xs: 2, md: 3 },
        pt: 2.5,
        pb: showKpis && kpis ? 2 : 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: ds.stickyHeader,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: showKpis && kpis ? 1.5 : 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {showMobileMenuButton && (
            <IconButton
              size="small"
              onClick={onOpenMobileMenu}
              aria-label="Open menu"
              sx={{ border: "1px solid", borderColor: "divider" }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ ...typeVariant("display"), color: "text.primary", lineHeight: 1.15 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          {hasData && (
            <Chip
              size="small"
              variant="outlined"
              label={`Source: ${providerLabel}`}
              sx={{ color: "text.secondary", borderColor: "divider" }}
            />
          )}
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {formatDate()}
          </Typography>
        </Box>
      </Box>
      {hasMultipleProviders && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1, mb: showKpis && kpis ? 1 : 0.25 }}>
          <Typography variant="caption" sx={{ color: "text.secondary", mr: 0.25 }}>
            Platform:
          </Typography>
          {["all", ...knownProviders].map(opt => (
            <Chip
              key={opt}
              size="small"
              color={providerFilter === opt ? "primary" : "default"}
              variant={providerFilter === opt ? "filled" : "outlined"}
              label={opt === "all" ? "Both" : opt}
              onClick={() => setProviderFilter(opt)}
              aria-pressed={providerFilter === opt}
              sx={{ fontWeight: 600, cursor: "pointer" }}
            />
          ))}
        </Box>
      )}
      {teamFilter && (
        <Box sx={{ mb: showKpis && kpis ? 1.25 : 0.25 }}>
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`Team filter (all pages): ${teamFilter}`}
            onDelete={() => setTeamFilter("")}
          />
        </Box>
      )}
      {showKpis && kpis}
    </Box>
  );
}
